import React, { useEffect } from 'react'
import { useFormik } from 'formik'
import { getUserId } from '../../helpers/auth'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { Button, Input, Modal, Select } from 'antd'
import Loader from '../../pages/pages/loader'
import {
  useCreateUserMutation,
  useUpdateUserMutation
} from '../../redux/apis/usersManagement/users/uMUsersApiSlice'
import GroupDropdown from '../GroupDropdown'
import PermissionsDropdown from '../PermissionsDropdown'

const EditCreateUser = ({ state, open, setOpen, record }) => {
  const [
    updateUser,
    { isLoading: isUpdating, isSuccess: SuccessUpdate, error: errorUpdate }
  ] = useUpdateUserMutation()
  const [
    addUser,
    { isLoading: isAdding, isSuccess: SuccessAdd, error: errorAdd }
  ] = useCreateUserMutation()

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: record?.first_name,
      last_name: record?.last_name,
      email: record?.email,
      username: record?.username,
      password: record?.password,
      groups: record?.groups || null,
      user_permissions: record?.user_permissions || null,
      is_staff: state === 'edit' ? record?.is_staff : false,
      is_superuser: state === 'edit' ? record?.is_superuser : false,
      is_active: state === 'edit' ? record?.is_active : true,
      last_updated_by: getUserId()
    },

    validationSchema: Yup.object({
      first_name: Yup.string().required('Please Enter First Name'),
      last_name: Yup.string().required('Please Enter Last Name'),
      email: Yup.string().required('Please Enter Email'),
      password: Yup.string(),
      is_active: Yup.boolean().required('Please Choose User Status')
    }),

    onSubmit: async values => {
      const payload = { ...values }

      // Remove password field if it's empty or undefined
      if (!payload.password) {
        delete payload.password
      }

      try {
        if (state === 'edit') {
          await updateUser({
            body: payload, // Send plain JSON
            id: record?.id
          }).unwrap()
        } else if (state === 'create') {
          await addUser(payload).unwrap()
        }
      } catch (err) {
        console.error(err)
      }
    }
  })

  useEffect(() => {
    if (errorAdd?.data) {
      const errorMessages = Object.values(errorAdd.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    } else if (errorUpdate?.data) {
      const errorMessages = Object.values(errorUpdate.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    }
  }, [errorAdd, errorUpdate])

  useEffect(() => {
    console.log(SuccessAdd, SuccessUpdate)
    if (SuccessAdd && state === 'create') {
      toast.success('User created successfully!')
      setOpen(false)
    } else if (SuccessUpdate && state === 'edit') {
      toast.success('User updated successfully!')
      setOpen(false)
    }
  }, [state, SuccessAdd, SuccessUpdate])
  return (
    <Modal
      className='customModal'
      open={open}
      title={state === 'edit' ? `Edit User` : 'Create User'}
      onCancel={() => setOpen(false)}
      footer={[
        <Button key='back' onClick={() => setOpen(false)}>
          Cancel
        </Button>,
        <Button
          key='submit'
          type='primary'
          onClick={() => {
            if (Object.keys(validation.errors).length !== 0) {
              Object.keys(validation.errors).map(key => {
                toast.error(validation.errors[key])
              })
              return
            }
            validation.handleSubmit()
          }}
        >
          Submit
        </Button>
      ]}
    >
      <Loader isLoading={isAdding || isUpdating} />

      <div className=' mb-3'>
        <label className="form-label">First Name</label>
        <Input
          type='text'
          name='first_name'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.first_name}
        />
        {validation.touched.first_name && validation.errors.first_name ? (
          <p className='text-danger'>{validation.errors.first_name}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <label className="form-label">Last Name</label>
        <Input
          type='text'
          name='last_name'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.last_name}
        />
        {validation.touched.last_name && validation.errors.last_name ? (
          <p className='text-danger'>{validation.errors.last_name}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <label className="form-label">Email</label>
        <Input
          type='text'
          name='email'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.email}
        />
        {validation.touched.email && validation.errors.email ? (
          <p className='text-danger'>{validation.errors.email}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <label className="form-label">User Name</label>
        <Input
          type='text'
          name='username'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.username}
        />
        {validation.touched.username && validation.errors.username ? (
          <p className='text-danger'>{validation.errors.username}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <label className="form-label">Password</label>
        <Input
          type='password'
          name='password'
          autoComplete='new-password'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.password}
        />
        {validation.touched.password && validation.errors.password ? (
          <p className='text-danger'>{validation.errors.password}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <GroupDropdown
          onChange={value => validation.setFieldValue('groups', value)}
          onBlur={validation.handleBlur}
          value={validation.values.groups}
          validation={validation}
          mode='multiple'
        />
        {validation.touched.groups && validation.errors.groups ? (
          <p className='text-danger'>{validation.errors.groups}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <PermissionsDropdown
          onChange={value =>
            validation.setFieldValue('user_permissions', value)
          }
          onBlur={validation.handleBlur}
          value={validation.values.user_permissions}
          validation={validation}
          mode='multiple'
        />
        {validation.touched.user_permissions &&
        validation.errors.user_permissions ? (
          <p className='text-danger'>{validation.errors.user_permissions}</p>
        ) : null}
      </div>
      <div className='mb-3'>
        <label className="form-label">Is Staff</label>
        <br />
        <Select
          name='is_staff'
          style={{
            width: '100%'
          }}
          onBlur={validation.handleBlur}
          onChange={e => {
            validation.setFieldValue('is_staff', e)
          }}
          value={validation.values?.is_staff}
          options={[
            { label: 'YES', value: true },
            { label: 'NO', value: false }
          ]}
        />
        {validation.touched.is_staff && validation.errors.is_staff ? (
          <p className='text-danger'>{validation.errors.is_staff}</p>
        ) : null}
      </div>
      <div className='mb-3'>
        <label className="form-label">Is Superuser</label>
        <br />
        <Select
          name='is_superuser'
          style={{
            width: '100%'
          }}
          onBlur={validation.handleBlur}
          onChange={e => {
            validation.setFieldValue('is_superuser', e)
          }}
          value={validation.values?.is_superuser}
          options={[
            { label: 'YES', value: true },
            { label: 'NO', value: false }
          ]}
        />
        {validation.touched.is_superuser && validation.errors.is_superuser ? (
          <p className='text-danger'>{validation.errors.is_superuser}</p>
        ) : null}
      </div>
      <div className='mb-3'>
        <label className="form-label">Status</label>
        <br />
        <Select
          name='is_active'
          style={{
            width: '100%'
          }}
          onBlur={validation.handleBlur}
          onChange={e => {
            validation.setFieldValue('is_active', e)
          }}
          value={validation.values?.is_active}
          options={[
            { label: 'Active', value: true },
            { label: 'InActive', value: false }
          ]}
        />
        {validation.touched.is_active && validation.errors.is_active ? (
          <p className='text-danger'>{validation.errors.is_active}</p>
        ) : null}
      </div>
    </Modal>
  )
}

export default EditCreateUser
